import { Link } from "@tanstack/react-router";
import { Pause, Play, SkipBack, SkipForward, X } from "lucide-react";
import { formatTime, usePlayer } from "@/lib/player/PlayerContext";

export function MiniPlayer() {
  const { current, isPlaying, toggle, next, previous, currentTime, duration, seek, clear, queue, index } = usePlayer();
  if (!current) return null;

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const hasPrev = index > 0;
  const hasNext = index >= 0 && index < queue.length - 1;

  return (
    <div
      role="region"
      aria-label="Audio player"
      className="fixed inset-x-0 bottom-16 z-40 border-t hairline bg-obsidian/95 backdrop-blur supports-[backdrop-filter]:bg-obsidian/80 lg:bottom-0"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {/* Progress scrubber */}
      <label className="block cursor-pointer">
        <span className="sr-only">Seek</span>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={(e) => seek(Number(e.target.value))}
          aria-label="Seek playback position"
          className="mini-player-range block h-1 w-full appearance-none bg-white/10"
          style={{
            background: `linear-gradient(to right, hsl(var(--gold)) ${pct}%, rgba(255,255,255,0.08) ${pct}%)`,
          }}
        />
      </label>

      <div className="mx-auto flex max-w-7xl items-center gap-3 px-3 py-2 sm:px-6 sm:py-3">
        {current.coverUrl ? (
          <img
            src={current.coverUrl}
            alt=""
            className="h-10 w-10 shrink-0 rounded-sm object-cover shadow-gold sm:h-12 sm:w-12"
          />
        ) : (
          <div className="h-10 w-10 shrink-0 rounded-sm border hairline bg-charcoal sm:h-12 sm:w-12" aria-hidden />
        )}

        <div className="min-w-0 flex-1">
          {current.href ? (
            <Link
              to={current.href}
              className="block truncate font-serif text-sm text-ivory hover:text-gold-luminous sm:text-base"
            >
              {current.title}
            </Link>
          ) : (
            <p className="truncate font-serif text-sm text-ivory sm:text-base">{current.title}</p>
          )}
          <p className="truncate text-[10px] uppercase tracking-[0.25em] text-gold sm:text-[11px]">
            {current.subtitle ?? "Buddha KA"}
          </p>
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          <span className="hidden text-[10px] tabular-nums text-muted-foreground sm:inline">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
          <button
            type="button"
            onClick={previous}
            disabled={!hasPrev}
            aria-label="Previous"
            className="rounded-full p-2 text-ivory/80 hover:text-gold disabled:opacity-30"
          >
            <SkipBack size={16} />
          </button>
          <button
            type="button"
            onClick={toggle}
            aria-label={isPlaying ? "Pause" : "Play"}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gold-gradient text-obsidian shadow-gold hover:shadow-halo"
          >
            {isPlaying ? <Pause size={16} /> : <Play size={16} className="translate-x-[1px]" />}
          </button>
          <button
            type="button"
            onClick={next}
            disabled={!hasNext}
            aria-label="Next"
            className="rounded-full p-2 text-ivory/80 hover:text-gold disabled:opacity-30"
          >
            <SkipForward size={16} />
          </button>
          <button
            type="button"
            onClick={clear}
            aria-label="Close player"
            className="ml-1 rounded-full p-2 text-ivory/60 hover:text-gold"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
